import axios, { AxiosInstance } from 'axios';
import { Task } from '../types/Task';
import { acquireAccessToken } from '../config/MsalConfig';

export type CreatePocRequest = {
  title: string;
  pocType: string;
  supplierName?: string;
};

export type PocDetailsResponse = {
  id: string;
  title?: string;
  pocType?: string;
  // other fields optional for now
};

export type BusinessJustificationRequest = {
  title: string;
  businessDomain: string;
  abstractDescription: string;
  abstractObjectives: string;
  abstractOutcomes: string;
  abstractEvidenceCriteria: string;
  motivation: string;
  initiationSource: string;
  valueCreation: string[];
};

export type ScorecardSubmissionRequest = {
  part1Question1: boolean | null;
  part1Question2: boolean | null;
  part1Question3: boolean | null;
  part1Question4: boolean | null;
  part1Question5: boolean | null;
  part1Question6: boolean | null;
  part1Question7: boolean | null;
  part1Question8: boolean | null;
  part1Question9: boolean | null;
  part1Question10: boolean | null;
  part1Question11: boolean | null;
  part1Question12: boolean | null;
  part1Question13: boolean | null;
  part1Question14: boolean | null;
  part1Question15: boolean | null;
  part2Question1: boolean | null;
  part2Question2: boolean | null;
  part2Question3: boolean | null;
};

export type ScorecardResponse = {
  pocId: string;
  totalScore: number;
  band: 'HIGHEST' | 'MEDIUM' | 'LOW';
  message: string;
  contactGdiRequired: boolean;
  // answers exist too; optional for now
};

export type SubmitInitiationRequest = {
  resultAccepted: boolean;
  facilitationSupportRequested: boolean;
};

export type FacilitationSupportRequest = {
  facilitationSupportRequested: boolean;
};

export type PhaseReadinessResponse = {
  pocId: string;
  currentPhase: string;
  currentStatus: string;
  message: string;
};


/** Matches BE PocSummaryResponse - list fields only (no score/source yet). */
export type PocSummaryResponse = {
  id: string;
  title: string;
  businessDomain?: string;
  currentPhase: string;
  currentStatus: string;
  requestorName?: string;
  requestorEmail?: string;
  plannedEndDate?: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
};

/** Spring Data Page JSON shape from GET /api/v1/pocs/my */
export type PageResponse<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
};

export type CreateSuccessCriterionRequest = {
  description: string;
  evaluationApproach: string;
  measurementMethod: string;
};

export type SuccessCriterionResponse = {
  id: string;
  pocId: string;
  description: string;
  evaluationApproach: string;
  measurementMethod: string;
  wasMet?: boolean | null;
  howMeasured?: string | null;
  resultDescription?: string | null;
};

export type EnvironmentRequest = {
  pocLocation: 'CUSTOMER_FACING' | 'ISS_INTERNAL';
  testSite:
    | 'REQUIRED_AND_AVAILABLE'
    | 'REQUIRED_NOT_IDENTIFIED'
    | 'NOT_REQUIRED';
};

export type EnvironmentResponse = {
  pocId: string;
  pocLocation: string;
  testSite: string;
};

export type NdaAssessmentRequest = {
  sharingConfidentialInfo: boolean;
  signedNdaFileRef: string | null;
  confirmed: boolean;
};

export type NdaAssessmentResponse = {
  pocId: string;
  sharingConfidentialInfo: boolean;
  ndaRequired: boolean;
  signedNdaFileRef: string | null;
  confirmed: boolean;
};

export type DocumentResponse = {
  id: string;
  pocId: string;
  fileRef: string;
  fileName?: string;
};

export type DataProtectionAssessmentRequest = {
  q1PersonalData: boolean;
  q2SpecialCategories: boolean;
  q3ThirdParties: boolean;
  q4OutsideEea: boolean;
};

export type DataProtectionAssessmentResponse = {
  pocId: string;
  q1PersonalData: boolean;
  q2SpecialCategories: boolean;
  q3ThirdParties: boolean;
  q4OutsideEea: boolean;
  dpaRequired: boolean;
  piaRequired: boolean;
  tiaRequired: boolean;
};

export type ItSecurityAssessmentRequest = {
  question1: boolean;
  question2: boolean;
  question3: boolean;
  question4: boolean;
  question5: boolean;
  question6: boolean;
  question7: boolean;
};

export type ItSecurityAssessmentResponse = {
  pocId: string;
  question1: boolean;
  question2: boolean;
  question3: boolean;
  question4: boolean;
  question5: boolean;
  question6: boolean;
  question7: boolean;
  totalScore: number;
  riskBand: 'HIGH' | 'MEDIUM' | 'LOW';
  requiredDocumentationActions?: string[];
};

export type LegalAssessmentRequest = {
  signedAgreementFileRef: string;
  contractCoverFileRef: string;
  declarationAccepted: boolean;
};

export type LegalAssessmentResponse = {
  pocId: string;
  agreementType: string;
  signedAgreementFileRef: string;
  contractCoverFileRef: string;
  declarationAccepted: boolean;
};

export type ProcurementAssessmentRequest = {
  noCommitmentDeclared: boolean;
  sourcingWillFollowDeclared: boolean;
  complianceConfirmed: boolean;
};

export type ProcurementAssessmentResponse = {
  pocId: string;
  noCommitmentDeclared: boolean;
  sourcingWillFollowDeclared: boolean;
  complianceConfirmed: boolean;
  supplierRegistrationAvailable: boolean;
};

export type CompletePreparationRequest = {
  declarationAccepted: boolean;
};

export type PreparationSummaryResponse = {
  pocId: string;
  currentPhase: string;
  currentStatus: string;
  message: string;
};

export type PlannedDuration =
  | 'ONE_MONTH'
  | 'THREE_MONTHS'
  | 'SIX_MONTHS'
  | 'CUSTOM';

export type TimelineRequest = {
  plannedDuration: PlannedDuration;
  plannedEndDate: string | null;
};

export type TimelineResponse = {
  pocId: string;
  plannedDuration: PlannedDuration;
  plannedEndDate: string;
};

export type CriterionEvaluationRequest = {
  wasMet: boolean;
  howMeasured: string;
  resultDescription: string;
};

export type OutcomeRequest = {
  successful: boolean;
  proceed: boolean;
  comment: string | null;
  nextStepType: 'IT_DEMAND' | 'PID_BUSINESS_CASE' | 'NONE';
};

export type OutcomeResponse = {
  pocId: string;
  successful: boolean;
  proceed: boolean;
  comment: string | null;
  nextStepType: 'IT_DEMAND' | 'PID_BUSINESS_CASE' | 'NONE';
};


class ApiService {
  private apiClient: AxiosInstance;
  private hostURL: string;

  constructor(baseURL, hostURL) {
    this.hostURL = hostURL;
    this.apiClient = axios.create({
      baseURL: baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.apiClient.interceptors.request.use(async (config) => {
      const token = await acquireAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }, (error) => {
      return Promise.reject(error);
    });

    this.apiClient.interceptors.response.use((response) => {
      return response;
    }, (error) => {
      return Promise.reject(error);
    });
  }

  async getTasks() {
    const response = await this.apiClient.get<Task[]>('/tasks');
    return response.data;
  }

  async createPoc(payload: CreatePocRequest): Promise<PocDetailsResponse> {
    const response = await this.apiClient.post<PocDetailsResponse>(
        '/api/v1/pocs',
        payload
    );
    return response.data;
  }

  async saveBusinessJustification(
    pocId: string,
    payload: BusinessJustificationRequest
  ): Promise<PocDetailsResponse> {
    const response = await this.apiClient.put<PocDetailsResponse>(
      `/api/v1/pocs/${pocId}/business-justification`,
      payload
    );
    return response.data;
  }

  async saveScorecard(
    pocId: string,
    payload: ScorecardSubmissionRequest
  ): Promise<ScorecardResponse> {
    const response = await this.apiClient.put<ScorecardResponse>(
      `/api/v1/pocs/${pocId}/scorecard`,
      payload
    );
    return response.data;
  }

  async saveFacilitationSupport(
    pocId: string,
    payload: FacilitationSupportRequest
  ): Promise<PocDetailsResponse> {
    const response = await this.apiClient.put<PocDetailsResponse>(
      `/api/v1/pocs/${pocId}/facilitation-support`,
      payload
    );
    return response.data;
  }

  async submitInitiation(
    pocId: string,
    payload: SubmitInitiationRequest
  ): Promise<PhaseReadinessResponse> {
    const response = await this.apiClient.post<PhaseReadinessResponse>(
      `/api/v1/pocs/${pocId}/submit-initiation`,
      payload
    );
    return response.data;
  }

  async createSuccessCriterion(
    pocId: string,
    payload: CreateSuccessCriterionRequest
  ): Promise<SuccessCriterionResponse> {
    const response = await this.apiClient.post<SuccessCriterionResponse>(
      `/api/v1/pocs/${pocId}/success-criteria`,
      payload
    );
    return response.data;
  }

  /** PUT /api/v1/pocs/{pocId}/environment */
  async saveEnvironment(
    pocId: string,
    payload: EnvironmentRequest
  ): Promise<EnvironmentResponse> {
    const response = await this.apiClient.put<EnvironmentResponse>(
      `/api/v1/pocs/${pocId}/environment`,
      payload
    );
    return response.data;
  }

  async uploadDocument(
    pocId: string,
    documentType: string,
    file: File
  ): Promise<DocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    const response = await this.apiClient.post<DocumentResponse>(
      `/api/v1/pocs/${pocId}/documents`,
      formData,
      {
        // Override JSON default so the browser can set multipart boundary
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }
  
  async saveNda(
    pocId: string,
    payload: NdaAssessmentRequest
  ): Promise<NdaAssessmentResponse> {
    const response = await this.apiClient.put<NdaAssessmentResponse>(
      `/api/v1/pocs/${pocId}/nda`,
      payload
    );
    return response.data;
  }

  async saveDataProtection(
    pocId: string,
    payload: DataProtectionAssessmentRequest
  ): Promise<DataProtectionAssessmentResponse> {
    const response = await this.apiClient.put<DataProtectionAssessmentResponse>(
      `/api/v1/pocs/${pocId}/data-protection`,
      payload
    );
    return response.data;
  }

  async saveItSecurity(
    pocId: string,
    payload: ItSecurityAssessmentRequest
  ): Promise<ItSecurityAssessmentResponse> {
    const response = await this.apiClient.put<ItSecurityAssessmentResponse>(
      `/api/v1/pocs/${pocId}/it-security`,
      payload
    );
    return response.data;
  }

  async saveLegal(
    pocId: string,
    payload: LegalAssessmentRequest
  ): Promise<LegalAssessmentResponse> {
    const response = await this.apiClient.put<LegalAssessmentResponse>(
      `/api/v1/pocs/${pocId}/legal`,
      payload
    );
    return response.data;
  }

  async saveProcurement(
    pocId: string,
    payload: ProcurementAssessmentRequest
  ): Promise<ProcurementAssessmentResponse> {
    const response = await this.apiClient.put<ProcurementAssessmentResponse>(
      `/api/v1/pocs/${pocId}/procurement`,
      payload
    );
    return response.data;
  }

  async completePreparation(
    pocId: string,
    payload: CompletePreparationRequest
  ): Promise<PreparationSummaryResponse> {
    const response = await this.apiClient.post<PreparationSummaryResponse>(
      `/api/v1/pocs/${pocId}/complete-preparation`,
      payload
    );
    return response.data;
  }

  async saveTimeline(
    pocId: string,
    payload: TimelineRequest
  ): Promise<TimelineResponse> {
    const response = await this.apiClient.put<TimelineResponse>(
      `/api/v1/pocs/${pocId}/timeline`,
      payload
    );
    return response.data;
  }

  /** POST /api/v1/pocs/{pocId}/complete-running — moves PoC to Evaluation. */
  async completeRunning(pocId: string): Promise<PhaseReadinessResponse> {
    const response = await this.apiClient.post<PhaseReadinessResponse>(
      `/api/v1/pocs/${pocId}/complete-running`
    );
    return response.data;
  }

  async getSuccessCriteria(
    pocId: string
  ): Promise<SuccessCriterionResponse[]> {
    const response = await this.apiClient.get<SuccessCriterionResponse[]>(
      `/api/v1/pocs/${pocId}/success-criteria`
    );
    return response.data;
  }
  
  async saveCriterionEvaluation(
    pocId: string,
    criterionId: string,
    payload: CriterionEvaluationRequest
  ): Promise<SuccessCriterionResponse> {
    const response = await this.apiClient.put<SuccessCriterionResponse>(
      `/api/v1/pocs/${pocId}/evaluation/criteria/${criterionId}`,
      payload
    );
    return response.data;
  }
  
  async saveOutcome(
    pocId: string,
    payload: OutcomeRequest
  ): Promise<OutcomeResponse> {
    const response = await this.apiClient.put<OutcomeResponse>(
      `/api/v1/pocs/${pocId}/outcome`,
      payload
    );
    return response.data;
  }



  /**
   * PoCs owned by the signed-in user (JWT). Do not send email as a query param.
   */
  async getMyPocs(params?: {
    page?: number;
    size?: number;
  }): Promise<PageResponse<PocSummaryResponse>> {
    const response = await this.apiClient.get<PageResponse<PocSummaryResponse>>(
      '/api/v1/pocs/my',
      {
        params: {
          page: params?.page ?? 0,
          size: params?.size ?? 50,
        },
      }
    );
    return response.data;
  }

  /**
   * Company-visible submitted PoCs (drafts excluded by BE).
   * Optional filters map to GET /api/v1/pocs/all query params.
   */
  async getAllPocs(params?: {
    page?: number;
    size?: number;
    phase?: string;
    status?: string;
    searchText?: string;
  }): Promise<PageResponse<PocSummaryResponse>> {
    const response = await this.apiClient.get<PageResponse<PocSummaryResponse>>(
      '/api/v1/pocs/all',
      {
        params: {
          page: params?.page ?? 0,
          size: params?.size ?? 50,
          phase: params?.phase || undefined,
          status: params?.status || undefined,
          searchText: params?.searchText || undefined,
        },
      }
    );
    return response.data;
  }

  async createTask(taskData: { name: string; description: string; proprity: string, category: string, targetDateRaw: Date, assigned: string }) {
    taskData["targetDate"] = new Date(taskData.targetDateRaw);
    const response = await this.apiClient.post('/tasks', taskData);
    return response.data;
  }

  async getTaskById(id: string) {
    const response = await this.apiClient.get(`/tasks/${id}`);
    return response.data;
  }

  async updateTask(id: string, taskData: { name: string; description: string }) {
    const response = await this.apiClient.put(`/tasks/${id}`, taskData);
    return response.data;
  }

  async deleteTask(id: string) {
    const response = await this.apiClient.delete(`/tasks/${id}`);
    return response.data;
  }

  getHostURL(): string {
    return this.hostURL;
  }
}

// Default to localhost if config is not present

const URLS = await axios.get('/config.json')
.then((response) => {
  return response.data; 
})

.catch((error) => {
  console.log("Cannot grab config");
  return {
    BACKEND_HOST: 'http://localhost:8080',
    HOST: 'http://localhost:3000'
  }
});

const apiService: ApiService = new ApiService(URLS.BACKEND_HOST, URLS.HOST);
export default apiService;